import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GI_MEDIA, GI_POSTERS } from "@/lib/brand";
const SHOTS = [...GI_POSTERS, ...GI_MEDIA];

export const GiGallery = ({ badge }) => {
  const [active, setActive] = useState(0);
  const shot = SHOTS[active];

  return (
    <div className="border-b border-white/10">
      <div className="relative aspect-[4/5] overflow-hidden bg-[#08080a]">
        <AnimatePresence mode="wait">
          <motion.div
            key={shot.src}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0"
          >
            <img
              src={shot.src}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full scale-110 object-cover opacity-25 blur-2xl"
            />
            <img
              src={shot.src}
              alt={shot.caption ? shot.label : `${shot.label} — female freemode ped`}
              data-testid="product-image-karate-gi"
              className={`absolute inset-0 h-full w-full ${shot.caption ? "object-cover" : "object-contain py-3"}`}
            />
          </motion.div>
        </AnimatePresence>
        {!shot.caption && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#08080a] via-[#08080a]/70 to-transparent" />
        )}
        <span className="absolute left-5 top-5 border border-white/18 bg-black/55 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-white/60 backdrop-blur-sm">
          {badge}
        </span>
        {!shot.caption && (
          <span
            data-testid="gi-gallery-caption"
            className="absolute bottom-4 left-0 right-0 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-white/50"
          >
            {shot.label} · mp_f_freemode_01
          </span>
        )}
      </div>

      <div className="flex items-stretch border-t border-white/10 bg-white/[0.03]">
        {SHOTS.map((m, i) => (
          <button
            key={m.id}
            onClick={() => setActive(i)}
            data-testid={`gi-swatch-${m.id}`}
            aria-label={m.label}
            className={`flex flex-1 items-center justify-center py-3.5 transition-colors duration-300 ${
              active === i ? "bg-white/8" : "hover:bg-white/5"
            }`}
          >
            {m.swatch ? (
              <span
                className={`h-4 w-4 rounded-full border transition-transform duration-300 ${
                  active === i ? "scale-110 border-white/80" : "border-white/25"
                }`}
                style={{ background: m.swatch }}
              />
            ) : (
              <span
                className={`font-mono text-[9px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                  active === i ? "text-white" : "text-white/35"
                }`}
              >
                {m.tab}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export const GiPoster = () => (
  <div className="grid gap-4 sm:grid-cols-2">
    {GI_POSTERS.map((p, i) => (
      <motion.figure
        key={p.id}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
        data-testid={i === 0 ? "gi-pack-poster" : "gi-pack-poster-male"}
        className="group relative overflow-hidden border border-white/10 bg-[#08080a]"
      >
        <img
          src={p.src}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-125 object-cover opacity-20 blur-3xl"
        />
        <img
          src={p.src}
          alt={p.alt}
          className="relative mx-auto block max-h-[760px] w-auto max-w-full object-contain transition-transform duration-[1.4s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.02]"
        />
      </motion.figure>
    ))}
  </div>
);

export const GiStrip = () => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
    {GI_MEDIA.map((m, i) => (
      <motion.figure
        key={m.id}
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.75, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
        data-testid={`gi-strip-${m.id}`}
        className="group relative overflow-hidden border border-white/10 bg-[#0a0a0d]"
      >
        <div className="relative aspect-[3/5] overflow-hidden">
          <img
            src={m.src}
            alt={`${m.label} — female freemode ped`}
            className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0a0a0d] via-transparent to-transparent" />
        </div>
        <figcaption className="flex items-center gap-2.5 border-t border-white/8 px-3 py-3">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full border border-white/25"
            style={{ background: m.swatch }}
          />
          <span className="truncate font-mono text-[9px] uppercase tracking-[0.14em] text-white/55">
            {m.label}
          </span>
        </figcaption>
      </motion.figure>
    ))}
  </div>
);
